import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../../lib/supabase';
import { siteKey } from '../../../../utils/sharedStorage';

type WithdrawalType = 'parrainage' | 'taches' | 'points';

interface WithdrawalRequest {
  id: string;
  amount: number;
  method: string;
  accountNumber: string;
  status: 'pending' | 'validated' | 'rejected';
  date: string;
  type: WithdrawalType;
  userName: string;
  userPhone: string;
  currency: string;
  paymentMethod: string;
  accountDetails: string;
}

const COUNTRY_CONFIG: Record<string, { currency: string }> = {
  'CI': { currency: 'FCFA' }, 'SN': { currency: 'FCFA' }, 'BJ': { currency: 'FCFA' },
  'ML': { currency: 'FCFA' }, 'TG': { currency: 'FCFA' }, 'BF': { currency: 'FCFA' },
  'GA': { currency: 'FCFA' }, 'CM': { currency: 'FCFA' }, 'CG': { currency: 'FCFA' },
  'CD': { currency: 'FC' }, 'GH': { currency: 'GH₵' },
};

const MIN_WITHDRAWAL = 3300;

export default function Retraits() {
  const [activeTab, setActiveTab] = useState<WithdrawalType>('parrainage');
  const [viewMode, setViewMode] = useState<'new' | 'history'>('new');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [currency, setCurrency] = useState('FCFA');
  const [userData, setUserData] = useState({
    realBalance: 0,
    balanceTaches: 0,
    balancePoints: 0,
    currency: 'FCFA',
    userName: '',
    userPhone: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBalances, setIsLoadingBalances] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState<string | null>(null);

  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const getCurrentBalance = () => {
    if (activeTab === 'parrainage') return userData.realBalance;
    if (activeTab === 'taches') return userData.balanceTaches;
    return userData.balancePoints;
  };

  const validateForm = (): boolean => {
    const parsedAmount = parseFloat(amount);
    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      showError('Veuillez saisir un montant valide.');
      return false;
    }
    if (parsedAmount < MIN_WITHDRAWAL) {
      showError(`Le montant minimum de retrait est ${MIN_WITHDRAWAL.toLocaleString()} ${currency}`);
      return false;
    }
    if (parsedAmount > getCurrentBalance()) {
      showError('Solde insuffisant pour ce retrait.');
      return false;
    }
    if (!method) {
      showError('Veuillez sélectionner une méthode de retrait.');
      return false;
    }
    if (!accountNumber.trim()) {
      showError('Veuillez saisir votre numéro de compte.');
      return false;
    }
    return true;
  };

  useEffect(() => {
    loadUserData();
    loadWithdrawals();

    let balanceChannel: BroadcastChannel | null = null;
    try {
      balanceChannel = new BroadcastChannel('balance_update');
      balanceChannel.onmessage = (event) => {
        const raw = localStorage.getItem('currentUser');
        if (!raw) return;
        try {
          const cu = JSON.parse(raw);
          if (event.data?.phone === cu.phone) {
            loadUserData(true);
          }
        } catch { /* ignore */ }
      };
    } catch { /* BroadcastChannel non supporté */ }

    const pollInterval = setInterval(() => {
      loadWithdrawals(true);
    }, 5000);

    if (supabase) {
      try {
        const ch = supabase
          .channel('withdrawal_updates')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'withdrawal_requests',
              filter: `site_id=eq.SUCCESSLINK_AGENCIA`,
            },
            () => {
              loadWithdrawals(true);
            }
          )
          .subscribe();
        channelRef.current = ch;
      } catch { /* ignore */ }
    }

    return () => {
      try { balanceChannel?.close(); } catch { /* ignore */ }
      clearInterval(pollInterval);
      if (supabase && channelRef.current) {
        try { supabase.removeChannel(channelRef.current); } catch { /* ignore */ }
      }
    };
  }, []);

  const showError = (msg: string) => {
    setErrorMsg(msg);
    setSuccessMsg('');
    setTimeout(() => setErrorMsg(''), 4000);
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setErrorMsg('');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const loadUserData = async (silent = false) => {
    if (!silent) setIsLoadingBalances(true);
    try {
      let user: Record<string, unknown> = {};
      try {
        const raw = localStorage.getItem('currentUser') || '{}';
        user = JSON.parse(raw);
      } catch { user = {}; }

      const userPhone = (user.phone as string) || localStorage.getItem('userPhone') || '';

      if (userPhone) {
        try {
          const rawUserData =
            localStorage.getItem(siteKey(`user_${userPhone}`)) ||
            localStorage.getItem(`user_${userPhone}`);
          if (rawUserData) {
            const freshData = JSON.parse(rawUserData);
            user = { ...user, ...freshData };
            localStorage.setItem('currentUser', JSON.stringify(user));
          }
        } catch { /* fallback */ }
      }

      const country = (user.country as string) || 'CI';
      const userName = (user.name as string) || (user.username as string) || '';
      const config = COUNTRY_CONFIG[country] || COUNTRY_CONFIG['CI'];
      setCurrency(config.currency);

      const rawParrainage = Number(user.balance) || 0;

      let balanceTaches = 0;
      let balancePoints = 0;
      let withdrawnParrainage = 0;
      try {
        const rawProfits = localStorage.getItem('userProfits') || '[]';
        let profits: Array<{ amount: number; type: string; phone?: string }> = [];
        try { profits = JSON.parse(rawProfits); } catch { profits = []; }
        if (!Array.isArray(profits)) profits = [];

        const myProfits = profits.filter(p => !p.phone || p.phone === userPhone);

        const grossTaches = myProfits
          .filter(p => {
            try {
              const type = (p.type || '').toLowerCase();
              return type.includes('tâche') || type.includes('tache');
            } catch { return false; }
          })
          .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

        const grossPoints = myProfits
          .filter(p => {
            try {
              const type = (p.type || '').toLowerCase();
              return type.includes('points') || type.includes('contacts') || type.includes('roue');
            } catch { return false; }
          })
          .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

        const rawWithdrawals1 = localStorage.getItem(siteKey('withdrawalRequests')) || '[]';
        const rawWithdrawals2 = localStorage.getItem('withdrawalRequests') || '[]';
        let allWithdrawals: Array<{ id: string; userPhone: string; type: string; amount: number; status: string }> = [];
        try {
          const w1 = JSON.parse(rawWithdrawals1);
          const w2 = JSON.parse(rawWithdrawals2);
          const combined = [...(Array.isArray(w1) ? w1 : []), ...(Array.isArray(w2) ? w2 : [])];
          const uniqueMap = new Map();
          combined.forEach((w: { id: string }) => uniqueMap.set(w.id, w));
          allWithdrawals = Array.from(uniqueMap.values());
        } catch { allWithdrawals = []; }

        const myWithdrawals = allWithdrawals.filter(
          w => w.userPhone === userPhone && (w.status === 'validated' || w.status === 'pending')
        );

        withdrawnParrainage = myWithdrawals
          .filter(w => w.type === 'parrainage')
          .reduce((sum, w) => sum + (Number(w.amount) || 0), 0);

        const withdrawnTaches = myWithdrawals
          .filter(w => w.type === 'taches')
          .reduce((sum, w) => sum + (Number(w.amount) || 0), 0);

        const withdrawnPoints = myWithdrawals
          .filter(w => w.type === 'points')
          .reduce((sum, w) => sum + (Number(w.amount) || 0), 0);

        balanceTaches = Math.max(0, grossTaches - withdrawnTaches);
        balancePoints = Math.max(0, grossPoints - withdrawnPoints);
      } catch { /* ignore */ }

      const realBalance = Math.max(0, rawParrainage - withdrawnParrainage);

      setUserData({ realBalance, balanceTaches, balancePoints, currency: config.currency, userName, userPhone });
    } catch (error) {
      console.error('Erreur loadUserData Retraits:', error);
    } finally {
      if (!silent) setIsLoadingBalances(false);
    }
  };

  const loadWithdrawals = async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) { setIsLoading(false); return; }

      const parsedUser = JSON.parse(raw);
      const userPhone = parsedUser.phone || '';
      const countryCode = parsedUser.country || 'CI';
      const config = COUNTRY_CONFIG[countryCode] || COUNTRY_CONFIG['CI'];
      setCurrency(config.currency);

      if (supabase) {
        try {
          const { data, error } = await supabase
            .from('withdrawal_requests')
            .select('*')
            .eq('site_id', 'SUCCESSLINK_AGENCIA')
            .eq('user_phone', userPhone)
            .order('created_at', { ascending: false });

          if (!error && data && data.length > 0) {
            const formattedWithdrawals: WithdrawalRequest[] = data.map((w: Record<string, unknown>) => ({
              id: w.id as string,
              userPhone: w.user_phone as string,
              amount: w.amount as number,
              method: w.method as string,
              accountNumber: w.account_number as string,
              accountDetails: (w.account_name as string) || '',
              status: w.status as 'pending' | 'validated' | 'rejected',
              date: (w.created_at as string) || '',
              type: (w.type as WithdrawalType) || 'parrainage',
              userName: (w.user_name as string) || '',
              currency: config.currency,
              paymentMethod: (w.method as string) || '',
            }));
            setWithdrawals(formattedWithdrawals);
            localStorage.setItem(siteKey('withdrawalRequests'), JSON.stringify(formattedWithdrawals));
            return;
          }
        } catch (err) {
          console.error('Erreur chargement retraits Supabase:', err);
        }
      }

      // Fallback localStorage
      const storedWithdrawals: WithdrawalRequest[] = JSON.parse(
        localStorage.getItem(siteKey('withdrawalRequests')) || '[]'
      );
      const userWithdrawals = storedWithdrawals.filter((w) => w.userPhone === userPhone);
      setWithdrawals(userWithdrawals);
    } catch (error) {
      console.error('Erreur chargement retraits:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) {
        showError('Utilisateur non connecté');
        setIsSubmitting(false);
        return;
      }

      const parsedUser = JSON.parse(raw);
      const userPhone = parsedUser.phone || '';
      const parsedAmount = parseFloat(amount);

      const newWithdrawal: WithdrawalRequest = {
        id: `WD-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        userPhone,
        amount: parsedAmount,
        method,
        accountNumber,
        accountDetails: accountNumber,
        status: 'pending',
        date: new Date().toISOString(),
        type: activeTab,
        userName: parsedUser.name || parsedUser.username || '',
        currency,
        paymentMethod: method,
      };

      if (supabase) {
        try {
          const { error } = await supabase
            .from('withdrawal_requests')
            .insert([{
              id: newWithdrawal.id,
              site_id: 'SUCCESSLINK_AGENCIA',
              user_phone: newWithdrawal.userPhone,
              amount: newWithdrawal.amount,
              method: newWithdrawal.method,
              account_number: newWithdrawal.accountNumber,
              account_name: newWithdrawal.accountDetails,
              status: newWithdrawal.status,
              type: newWithdrawal.type,
              created_at: newWithdrawal.date,
            }]);

          if (!error) {
            showSuccess('✅ Demande de retrait envoyée avec succès !');
            setAmount('');
            setMethod('');
            setAccountNumber('');
            await loadWithdrawals(true);
            setIsSubmitting(false);
            return;
          }
        } catch (err) {
          console.error('Erreur sauvegarde retrait Supabase:', err);
        }
      }

      // Fallback localStorage
      const storedWithdrawals: WithdrawalRequest[] = JSON.parse(
        localStorage.getItem(siteKey('withdrawalRequests')) || '[]'
      );
      storedWithdrawals.push(newWithdrawal);
      localStorage.setItem(siteKey('withdrawalRequests'), JSON.stringify(storedWithdrawals));

      showSuccess('✅ Demande de retrait envoyée avec succès !');
      setAmount('');
      setMethod('');
      setAccountNumber('');
      await loadWithdrawals(true);
    } catch (error) {
      console.error('Erreur soumission retrait:', error);
      showError('❌ Erreur lors de la soumission. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelWithdrawal = async (withdrawalId: string) => {
    setCancellingId(withdrawalId);
    try {
      try {
        const raw1 = localStorage.getItem(siteKey('withdrawalRequests')) || '[]';
        let saved1: WithdrawalRequest[] = [];
        try { saved1 = JSON.parse(raw1); } catch { saved1 = []; }
        localStorage.setItem(siteKey('withdrawalRequests'), JSON.stringify(saved1.filter(w => w.id !== withdrawalId)));

        const raw2 = localStorage.getItem('withdrawalRequests') || '[]';
        let saved2: WithdrawalRequest[] = [];
        try { saved2 = JSON.parse(raw2); } catch { saved2 = []; }
        localStorage.setItem('withdrawalRequests', JSON.stringify(saved2.filter(w => w.id !== withdrawalId)));
      } catch { /* ignore */ }

      await loadWithdrawals(true);
      setShowCancelConfirm(null);
      showSuccess('✅ Demande de retrait annulée avec succès !');
    } catch (error) {
      console.error('Erreur annulation retrait:', error);
      showError('Erreur lors de l\'annulation. Veuillez réessayer.');
    } finally {
      setCancellingId(null);
    }
  };

  const getTabLabel = () => {
    switch (activeTab) {
      case 'parrainage': return 'Parrainage';
      case 'taches': return 'Tâches';
      case 'points': return 'Points';
      default: return '';
    }
  };

  const getTabIcon = () => {
    switch (activeTab) {
      case 'parrainage': return 'ri-user-add-line';
      case 'taches': return 'ri-task-line';
      case 'points': return 'ri-star-line';
      default: return 'ri-wallet-line';
    }
  };

  const filteredWithdrawals = withdrawals.filter(w => w.type === activeTab && w.userPhone === userData.userPhone);
  const pendingWithdrawals = filteredWithdrawals.filter(w => w.status === 'pending');
  const completedWithdrawals = filteredWithdrawals.filter(w => w.status !== 'pending');

  const formatDisplayDate = (dateString: string) => {
    try { return new Date(dateString).toLocaleDateString('fr-FR'); }
    catch { return dateString; }
  };

  const getOperatorColor = (m: string) => {
    const ml = m.toLowerCase();
    if (ml.includes('orange')) return 'bg-orange-500 text-white';
    if (ml.includes('mtn')) return 'bg-yellow-400 text-gray-900';
    if (ml.includes('moov')) return 'bg-blue-500 text-white';
    if (ml.includes('wave')) return 'bg-blue-600 text-white';
    if (ml.includes('airtel')) return 'bg-red-500 text-white';
    if (ml.includes('free')) return 'bg-purple-500 text-white';
    if (ml.includes('t-money')) return 'bg-green-500 text-white';
    if (ml.includes('usdt') || ml.includes('crypto')) return 'bg-teal-600 text-white';
    return 'bg-gray-600 text-white';
  };

  const calculateNetAmount = (grossAmount: number) => Math.max(0, grossAmount - 400);

  return (
    <div className="space-y-6">
      {(isLoadingBalances || isLoading) && (
        <div className="fixed top-4 right-4 z-50 bg-teal-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin"></i>
          <span className="text-sm font-semibold">Chargement...</span>
        </div>
      )}
      {isSubmitting && (
        <div className="fixed top-4 right-4 z-50 bg-teal-700 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin"></i>
          <span className="text-sm font-semibold">Envoi en cours...</span>
        </div>
      )}

      {errorMsg && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-300 rounded-xl text-red-700 text-sm font-semibold">
          <i className="ri-error-warning-line text-lg flex-shrink-0"></i>
          <span>{errorMsg}</span>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-300 rounded-xl text-green-700 text-sm font-semibold">
          <i className="ri-checkbox-circle-line text-lg flex-shrink-0"></i>
          <span>{successMsg}</span>
        </div>
      )}

      <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-8 text-white shadow-lg">
        <h2 className="text-2xl font-bold mb-2">Retraits</h2>
        <p className="text-teal-100">Retirez vos gains selon leur source</p>
      </div>

      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-90 mb-1 font-semibold">💰 Revenus cumulés totaux</p>
            <p className="text-3xl font-extrabold">
              {(userData.realBalance + userData.balanceTaches + userData.balancePoints).toLocaleString()}{' '}
              <span className="text-lg font-normal">{userData.currency}</span>
            </p>
            <p className="text-xs opacity-75 mt-1">
              Parrainage: {userData.realBalance.toLocaleString()} · Tâches: {userData.balanceTaches.toLocaleString()} · Points: {userData.balancePoints.toLocaleString()}
            </p>
          </div>
          <button
            onClick={() => loadUserData()}
            disabled={isLoadingBalances}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold text-sm transition cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${isLoadingBalances ? 'animate-spin' : ''}`}></i>
            Actualiser
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white rounded-xl p-2 shadow-lg flex gap-2">
        {(['parrainage', 'taches', 'points'] as WithdrawalType[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-3 rounded-lg font-bold text-sm transition whitespace-nowrap cursor-pointer ${
              activeTab === tab
                ? tab === 'parrainage' ? 'bg-orange-500 text-white shadow-md'
                  : tab === 'taches' ? 'bg-teal-500 text-white shadow-md'
                  : 'bg-yellow-500 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <i className={`${tab === 'parrainage' ? 'ri-user-add-line' : tab === 'taches' ? 'ri-task-line' : 'ri-star-line'} mr-1`}></i>
            {tab === 'parrainage' ? 'Parrainage' : tab === 'taches' ? 'Tâches' : 'Points'}
          </button>
        ))}
      </div>

      {/* Solde onglet actif */}
      <div className={`rounded-xl p-6 text-white shadow-lg ${
        activeTab === 'parrainage' ? 'bg-gradient-to-br from-orange-500 to-orange-600' :
        activeTab === 'taches' ? 'bg-gradient-to-br from-teal-500 to-teal-600' :
        'bg-gradient-to-br from-yellow-500 to-yellow-600'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-90 mb-1">Solde disponible — {getTabLabel()}</p>
            <p className="text-3xl font-extrabold">
              {getCurrentBalance().toLocaleString()} <span className="text-lg font-normal">{userData.currency}</span>
            </p>
            <p className="text-xs opacity-75 mt-2">
              {activeTab === 'parrainage' && '💰 Commissions créditées automatiquement à chaque activation de filleul'}
              {activeTab === 'taches' && '✅ Gains de vos tâches accomplies'}
              {activeTab === 'points' && '⭐ Gains de points, contacts et roue'}
            </p>
          </div>
          <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full">
            <i className={`${getTabIcon()} text-4xl`}></i>
          </div>
        </div>
      </div>

      {/* Basculer vue */}
      <div className="bg-white rounded-xl p-2 shadow-lg flex gap-2">
        <button
          onClick={() => setViewMode('new')}
          className={`flex-1 px-4 py-3 rounded-lg font-bold text-sm transition whitespace-nowrap cursor-pointer ${viewMode === 'new' ? 'bg-teal-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          <i className="ri-add-circle-line mr-1"></i> Nouveau retrait
        </button>
        <button
          onClick={() => setViewMode('history')}
          className={`flex-1 px-4 py-3 rounded-lg font-bold text-sm transition whitespace-nowrap cursor-pointer relative ${viewMode === 'history' ? 'bg-teal-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          <i className="ri-history-line mr-1"></i> Historique
          {pendingWithdrawals.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {pendingWithdrawals.length}
            </span>
          )}
        </button>
      </div>

      {/* Formulaire de retrait */}
      {viewMode === 'new' && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <i className="ri-bank-card-line text-teal-600"></i> Demander un retrait — {getTabLabel()}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Montant à retirer</label>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="Ex: 5000"
                  min={MIN_WITHDRAWAL}
                  max={getCurrentBalance()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 outline-none pr-20"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-500">{userData.currency}</span>
              </div>
              <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
                <i className="ri-information-line text-amber-500 text-base mt-0.5 flex-shrink-0"></i>
                <p className="text-xs text-amber-700 font-semibold">
                  Minimum : <strong>{MIN_WITHDRAWAL.toLocaleString()} {userData.currency}</strong> — Disponible : <strong>{getCurrentBalance().toLocaleString()} {userData.currency}</strong>
                </p>
              </div>
              {amount && parseFloat(amount) >= MIN_WITHDRAWAL && parseFloat(amount) <= getCurrentBalance() && (
                <div className="mt-2 flex items-start gap-2 bg-green-50 border border-green-300 rounded-lg px-3 py-2">
                  <i className="ri-checkbox-circle-line text-green-500 text-base mt-0.5 flex-shrink-0"></i>
                  <div className="flex-1">
                    <p className="text-xs text-green-700 font-semibold">
                      ✅ Montant valide — Vous recevrez <strong>{calculateNetAmount(parseFloat(amount)).toLocaleString()} {userData.currency}</strong>
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      (Montant demandé: {parseFloat(amount).toLocaleString()} F - Frais de retrait: 400 F)
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Méthode de retrait</label>
              <select
                value={method}
                onChange={e => setMethod(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 outline-none cursor-pointer"
              >
                <option value="">— Sélectionnez une méthode —</option>
                <optgroup label="📱 Mobile Money — Afrique de l'Ouest (FCFA)">
                  <option value="Orange Money">Orange Money</option>
                  <option value="MTN Money">MTN Money</option>
                  <option value="Moov Money">Moov Money</option>
                  <option value="Wave">Wave</option>
                  <option value="Airtel Money">Airtel Money</option>
                  <option value="Free Money">Free Money</option>
                  <option value="T-Money">T-Money (Togo)</option>
                </optgroup>
                <optgroup label="🇬🇭 Mobile Money — Ghana (GH₵)">
                  <option value="MTN Mobile Money Ghana">MTN Mobile Money (Ghana)</option>
                </optgroup>
                <optgroup label="💎 Crypto">
                  <option value="USDT BEP20">USDT BEP20 (Binance Smart Chain)</option>
                </optgroup>
              </select>
            </div>

            {method ? (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {method.includes('USDT') ? 'Adresse du portefeuille (Wallet)' : 'Numéro de compte Mobile Money'}
                </label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={e => setAccountNumber(e.target.value)}
                  placeholder={method.includes('USDT') ? 'Ex: 0xABC123...' : 'Ex: +2250700000000'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 outline-none"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Numéro de compte</label>
                <input type="text" disabled placeholder="Sélectionnez d'abord une méthode"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 cursor-not-allowed" />
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full font-bold py-3 rounded-lg transition cursor-pointer whitespace-nowrap ${isSubmitting ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
            >
              {isSubmitting
                ? <><i className="ri-loader-4-line animate-spin mr-2"></i>Envoi en cours...</>
                : <><i className="ri-send-plane-line mr-2"></i>Soumettre la demande</>
              }
            </button>

            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <i className="ri-information-line text-amber-500 text-lg mt-0.5 flex-shrink-0"></i>
              <div className="flex-1">
                <p className="text-xs text-amber-700 font-semibold">
                  Des frais de retrait de <strong>400 F</strong> sont appliqués sur chaque demande.
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Le montant net que vous recevrez sera automatiquement calculé après déduction des frais.
                </p>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Historique */}
      {viewMode === 'history' && (
        <div className="space-y-6">
          {pendingWithdrawals.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <i className="ri-time-line text-orange-600"></i> Retraits en attente — {getTabLabel()}
                <span className="ml-auto bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-bold">
                  {pendingWithdrawals.length}
                </span>
              </h3>
              <div className="space-y-3">
                {pendingWithdrawals.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(withdrawal => (
                  <div key={withdrawal.id} className="border-2 border-orange-300 bg-orange-50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="mb-2">
                          <p className="font-bold text-gray-800 text-lg">{withdrawal.amount.toLocaleString()} {userData.currency}</p>
                          <p className="text-sm text-green-700 font-semibold mt-1">
                            💰 Vous recevrez: <strong>{calculateNetAmount(withdrawal.amount).toLocaleString()} {userData.currency}</strong>
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">(Frais de retrait: 400 F déduits)</p>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${getOperatorColor(withdrawal.method)}`}>
                            <i className="ri-smartphone-line mr-1"></i>
                            {withdrawal.method}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-white/60 rounded-lg px-3 py-2 border border-orange-200">
                          <i className="ri-phone-line text-orange-600 text-base"></i>
                          <span className="text-sm font-mono font-semibold text-gray-800">{withdrawal.accountNumber}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 ml-3">
                        <span className="px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap bg-yellow-100 text-yellow-700">
                          ⏳ En attente
                        </span>
                        <button
                          onClick={() => setShowCancelConfirm(withdrawal.id)}
                          disabled={cancellingId === withdrawal.id}
                          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-bold transition cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          {cancellingId === withdrawal.id
                            ? <><i className="ri-loader-4-line animate-spin"></i> Annulation...</>
                            : <><i className="ri-close-circle-line"></i> Annuler</>
                          }
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {formatDisplayDate(withdrawal.date)}</span>
                      <span className={`px-2 py-1 rounded-full font-semibold ${
                        activeTab === 'parrainage' ? 'bg-orange-100 text-orange-700' :
                        activeTab === 'taches' ? 'bg-teal-100 text-teal-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>{getTabLabel()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-history-line text-teal-600"></i> Historique complet — {getTabLabel()}
            </h3>
            {completedWithdrawals.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-inbox-line text-5xl text-gray-200 mb-3"></i>
                <p className="text-gray-500">Aucun retrait {getTabLabel().toLowerCase()} validé ou rejeté</p>
              </div>
            ) : (
              <div className="space-y-3">
                {completedWithdrawals.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(withdrawal => (
                  <div key={withdrawal.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="mb-2">
                          <p className="font-bold text-gray-800 text-lg">{withdrawal.amount.toLocaleString()} {userData.currency}</p>
                          {withdrawal.status === 'validated' && (
                            <>
                              <p className="text-sm text-green-700 font-semibold mt-1">
                                💰 Montant reçu: <strong>{calculateNetAmount(withdrawal.amount).toLocaleString()} {userData.currency}</strong>
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">(Frais de retrait: 400 F déduits)</p>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${getOperatorColor(withdrawal.method)}`}>
                            <i className="ri-smartphone-line mr-1"></i>
                            {withdrawal.method}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                          <i className="ri-phone-line text-gray-600 text-base"></i>
                          <span className="text-sm font-mono font-semibold text-gray-800">{withdrawal.accountNumber}</span>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ml-3 ${
                        withdrawal.status === 'validated' ? 'bg-green-100 text-green-700' :
                        withdrawal.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {withdrawal.status === 'validated' ? '✅ Validé' : withdrawal.status === 'rejected' ? '❌ Rejeté' : '⏳ En attente'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {formatDisplayDate(withdrawal.date)}</span>
                      <span className={`px-2 py-1 rounded-full font-semibold ${
                        activeTab === 'parrainage' ? 'bg-orange-100 text-orange-700' :
                        activeTab === 'taches' ? 'bg-teal-100 text-teal-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>{getTabLabel()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Popup confirmation annulation */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-lg">Annuler ce retrait ?</h3>
                <p className="text-sm text-gray-600">Cette action est irréversible</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-6">
              Êtes-vous sûr de vouloir annuler cette demande de retrait ? Vous pourrez en créer une nouvelle par la suite.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(null)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition cursor-pointer whitespace-nowrap"
              >
                Non, garder
              </button>
              <button
                onClick={() => handleCancelWithdrawal(showCancelConfirm)}
                disabled={cancellingId === showCancelConfirm}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancellingId === showCancelConfirm ? 'Annulation...' : 'Oui, annuler'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
